/**
 * Rakhi Promo — auto-add the free region-appropriate gift card
 * ---------------------------------------------------------------
 * The $50 / £50 gift card variant becomes FREE once a cart qualifies for
 * its region's Rakhi discount (whether that's HAPPYRAKHRI-US/-CA/-UK run
 * as Discount Codes, or the equivalent Automatic Discounts). A discount
 * can only reduce the price of a line that's ALREADY in the cart — it can
 * never add a new line item on its own. This script is the missing piece:
 * it watches the cart and adds (or removes) the correct region's gift
 * card variant so the discount has something to apply its $0 price to.
 *
 * Qualification logic (in priority order):
 *  1. If Shopify reports one of our HAPPYRAKHRI-* codes as `applicable`
 *     on the cart (customer typed a code), trust that.
 *  2. Otherwise (the normal case with Automatic Discounts, where nothing
 *     shows up in `cart.discount_codes` until a code is typed), fall back
 *     to a client-side subtotal check against each region's
 *     ~$250-equivalent threshold, using the cart's own presentment
 *     currency totals from /cart.js.
 *
 * On the regular website, this uses the Ajax Cart API directly AND feeds
 * the response back into the theme's own cart-drawer rendering
 * (`cart-drawer`'s `renderContents`), the same way the theme's native
 * add-to-cart code does. Earlier versions of this script only wrote to
 * the cart via a plain fetch with no re-render, so a gift card could be
 * sitting in the cart already but invisible in the drawer until the
 * customer refreshed the page — this version renders the same section
 * HTML the theme itself would, so it shows up immediately.
 *
 * TAPCART DISABLED FOR NOW (2026-08-28): this script previously detected
 * `window.Tapcart.action` and routed through Tapcart's own cart bridge
 * when present, for use inside the Tapcart app. That detection turned out
 * to be unreliable on the regular website: Tapcart's own "cart-sync" app
 * embed loads `window.Tapcart.action` on the public site too (not just
 * inside the app), and calling it there throws (no native bridge to
 * receive it) — which was silently aborting this script's whole sync and
 * is why the free gift wasn't being added on the live website. Tapcart
 * support is on hold until that's sorted out properly; every add/remove
 * below now goes through the plain website Ajax Cart API unconditionally.
 * The old `isTapcart` / `tapcartAddGift` / `tapcartRemoveGift` functions
 * are left in the file, unused, to pick back up later.
 *
 * Safety rules baked in:
 *  - Only ever touches a gift card line that THIS script added (tagged
 *    with a `_promo` line item property/attribute) — a gift card a
 *    customer deliberately bought (e.g. from the "sisters of TSR"
 *    campaign) is never removed.
 *  - Only one auto-added gift card line at a time, matching whichever
 *    region actually qualifies right now.
 *  - Re-checks after every cart mutation and on every page load, so
 *    removing an item that drops the cart back under threshold (or
 *    removing the code) removes the free gift too.
 *
 * KNOWN LIMITATION: the subtotal fallback (path 2 above) assumes the
 * cart's presentment currency for the "everyone else" bucket is close to
 * USD (i.e. the customer is in the US). Shopify Markets customers outside
 * US/CA/UK (e.g. AU, EU, IN) will have their own presentment currency and
 * the flat 250-unit comparison below won't reflect the real USD-converted
 * threshold for those currencies. Tighten per-currency thresholds here if
 * the promo needs to be precise for those markets too.
 */
(function () {
  'use strict';

  // Grab a reference to the real, un-wrapped fetch before anything below
  // wraps window.fetch — all of this script's own network calls use this,
  // so they never re-trigger this script's own "did the cart change?"
  // listener (which only cares about OTHER code's cart/add/change calls).
  var nativeFetch = window.fetch.bind(window);

  // ---- CONFIG -------------------------------------------------------
  var PROMO_TAG = 'rakhi_free_gift_promo';
  var LAST_ATTEMPT_KEY = 'tsr_rakhi_discount_attempt';

  // Exact variant GIDs pulled from Shopify for the 3 region gift cards.
  var GIFT_VARIANTS = {
    ca: { id: 42999665426503, label: 'CA $50 Gift Card' },      // ca-gift-card, $50 CAD variant
    uk: { id: 42999801872455, label: 'UK £50 Gift Card' },      // digital-gift-card, £50 GBP variant
    default: { id: 42999800954951, label: 'US $50 Gift Card' }  // e-gift-card, $50 USD variant (everyone else)
  };

  // The 3 discount codes created in Shopify Admin, mapped to the gift
  // card variant each one unlocks. Used when a code has been typed in.
  var CODE_GIFT_MAP = {
    'HAPPYRAKHRI-CA': GIFT_VARIANTS.ca,
    'HAPPYRAKHRI-UK': GIFT_VARIANTS.uk,
    'HAPPYRAKHRI-US': GIFT_VARIANTS.default
  };

  // Fallback client-side thresholds (in cents of the cart's own
  // presentment currency) for when the promo runs as an Automatic
  // Discount and there's no code to check applicability against.
  var SUBTOTAL_THRESHOLD_CENTS = {
    ca: 25000,
    uk: 25000,
    default: 25000
  };
  // ---------------------------------------------------------------------

  function getMarket() {
    var seg = window.location.pathname.split('/')[1] || '';
    if (seg === 'en-ca') return 'ca';
    if (seg === 'en-uk') return 'uk';
    return 'default'; // en-us, en-au, en-eu, en-in, or no locale prefix
  }

  function allGiftVariantIds() {
    return Object.keys(GIFT_VARIANTS).map(function (k) { return GIFT_VARIANTS[k].id; });
  }

  // ---- Tapcart bridge detection --------------------------------------
  function isTapcart() {
    return typeof window !== 'undefined' &&
      window.Tapcart &&
      typeof window.Tapcart.action === 'function';
  }

  function toVariantGid(variantId) {
    return 'gid://shopify/ProductVariant/' + variantId;
  }

  // ---- Reading the cart (same on web + Tapcart — both reflect the same
  // underlying Shopify cart) -------------------------------------------
  function fetchCart() {
    return nativeFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // ---- Rendering the storefront cart drawer after a change we made ---
  // Mirrors what the theme's own add-to-cart code does: request the
  // section HTML alongside the cart mutation, then hand it to
  // <cart-drawer>'s own renderContents(). `open: false` so we never pop
  // the drawer open just because this script silently added/removed a
  // gift line in the background.
  function getCartDrawerEl() {
    var el = document.querySelector('cart-drawer');
    return (el && typeof el.renderContents === 'function' && typeof el.getSectionsToRender === 'function')
      ? el
      : null;
  }

  function getSectionsParam(cartDrawerEl) {
    if (!cartDrawerEl) return null;
    var sections = cartDrawerEl.getSectionsToRender().map(function (s) { return s.section; });
    return sections.length ? sections : null;
  }

  function renderCartUI(response) {
    var cartDrawerEl = getCartDrawerEl();
    if (cartDrawerEl && response && response.sections) {
      cartDrawerEl.renderContents(response, false);
      return true;
    }
    return false;
  }

  // ---- Web (Ajax Cart API) add/remove --------------------------------
  function webAddGift(variantId) {
    var cartDrawerEl = getCartDrawerEl();
    var sections = getSectionsParam(cartDrawerEl);
    var body = { items: [{ id: variantId, quantity: 1, properties: { _promo: PROMO_TAG } }] };
    if (sections) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    return nativeFetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(function (response) {
      renderCartUI(response);
      return response;
    });
  }

  function webRemoveLine(line) {
    var cartDrawerEl = getCartDrawerEl();
    var sections = getSectionsParam(cartDrawerEl);
    var body = { line: line, quantity: 0 };
    if (sections) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    return nativeFetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(function (response) {
      renderCartUI(response);
      return response;
    });
  }

  // ---- Tapcart bridge add/remove -------------------------------------
  // Tapcart's native cart isn't reliably updated by a plain fetch to the
  // Ajax Cart API — it needs to go through Tapcart's own action bridge.
  // Tapcart's own UI re-renders itself; we don't touch the DOM here.
  //
  // Variant ID format is intentionally inconsistent between actions here,
  // matching Tapcart's own documentation/support guidance for each:
  //  - cart/add: Tapcart's own support bot gave us the exact code to use,
  //    which passes the PLAIN numeric Shopify variant ID (not a GID).
  //  - cart/remove: Tapcart's docs page for this action shows a full
  //    `gid://shopify/ProductVariant/...` string.
  function tapcartAddGift(variantId) {
    return window.Tapcart.action('cart/add', {
      lineItems: [{
        variantId: variantId,
        quantity: 1,
        attributes: [{ key: '_promo', value: PROMO_TAG }]
      }]
    });
  }

  function tapcartRemoveGift(variantId) {
    return window.Tapcart.action('cart/remove', {
      lineItems: [{ variantId: toVariantGid(variantId) }]
      // no quantity → removes all quantity of that variant
    });
  }

  // ---- Unified add/remove ---------------------------------------------
  // NOTE (2026-08-28): Tapcart integration is intentionally disabled for
  // now — Tapcart's own "cart-sync" app embed loads `window.Tapcart.action`
  // on the regular website too, which was causing the free gift to
  // silently fail to add on the live site (the bridge call throws outside
  // the actual Tapcart app, since there's no native handler to receive it).
  // Until the Tapcart side of this is revisited, EVERY add/remove goes
  // through the plain website Ajax Cart API, full stop — no Tapcart branch,
  // no detection, no fallback logic to reason about. The tapcartAddGift /
  // tapcartRemoveGift / isTapcart functions above are left in place,
  // unused, for whenever Tapcart support gets picked back up.
  function addGift(variantId) {
    return webAddGift(variantId);
  }

  function removeGiftLine(line, variantId) {
    return webRemoveLine(line);
  }

  // Path 1: an applicable HAPPYRAKHRI-* code already on the cart.
  function getGiftUnlockedByCode(cart) {
    var codes = cart.discount_codes || [];
    for (var i = 0; i < codes.length; i++) {
      var entry = codes[i];
      if (!entry || !entry.applicable || !entry.code) continue;
      var variant = CODE_GIFT_MAP[entry.code.toUpperCase()];
      if (variant) return variant;
    }
    return null;
  }

  // Path 2: no code on the cart — fall back to our own subtotal read,
  // for when the promo is running as an Automatic Discount.
  function getGiftUnlockedBySubtotal(cart) {
    var market = getMarket();
    var giftIds = allGiftVariantIds();
    var qualifyingSubtotal = 0;

    cart.items.forEach(function (item) {
      if (giftIds.indexOf(item.variant_id) === -1) {
        qualifyingSubtotal += item.line_price;
      }
    });

    var threshold = SUBTOTAL_THRESHOLD_CENTS[market] || SUBTOTAL_THRESHOLD_CENTS.default;
    if (qualifyingSubtotal >= threshold) {
      return GIFT_VARIANTS[market] || GIFT_VARIANTS.default;
    }
    return null;
  }

  function getUnlockedGift(cart) {
    return getGiftUnlockedByCode(cart) || getGiftUnlockedBySubtotal(cart);
  }

  var syncing = false;
  var pendingResync = false;

  function sync() {
    if (syncing) {
      pendingResync = true;
      return;
    }
    syncing = true;

    fetchCart().then(function (cart) {
      showDiscountFeedback(cart);

      var giftIds = allGiftVariantIds();
      var autoGiftLines = []; // { line, variant_id }

      cart.items.forEach(function (item, idx) {
        var isGiftVariant = giftIds.indexOf(item.variant_id) !== -1;
        var isAutoAdded = item.properties && item.properties._promo === PROMO_TAG;
        if (isGiftVariant && isAutoAdded) {
          autoGiftLines.push({ line: idx + 1, variant_id: item.variant_id });
        }
        // A manually-purchased gift card (no _promo property) is simply
        // never touched here — it's not removed or counted.
      });

      var wanted = getUnlockedGift(cart); // null if nothing currently qualifies
      var hasCorrectGift = wanted && autoGiftLines.some(function (l) { return l.variant_id === wanted.id; });
      var wrongGiftLines = autoGiftLines.filter(function (l) { return !wanted || l.variant_id !== wanted.id; });

      var chain = Promise.resolve();

      // Clean up any auto-gift that no longer matches (wrong region, cart
      // dropped below threshold, or the code/discount no longer applies).
      wrongGiftLines.forEach(function (l) {
        chain = chain.then(function () { return removeGiftLine(l.line, l.variant_id); });
      });

      if (wanted && !hasCorrectGift) {
        chain = chain.then(function () { return addGift(wanted.id); });
      }

      return chain;
    }).then(function () {
      syncing = false;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      if (pendingResync) {
        pendingResync = false;
        sync();
      }
    }).catch(function (err) {
      syncing = false;
      console.error('Free gift promo sync error', err);
    });
  }

  document.addEventListener('DOMContentLoaded', sync);
  document.addEventListener('cart:updated', sync);
  document.addEventListener('shopify:section:load', sync);

  // Detect OTHER code's cart mutations (the theme's own add-to-cart /
  // quantity-change calls) so we know when to re-check. This wraps
  // window.fetch, but this script's OWN network calls all go through
  // `nativeFetch` above and never touch this wrapper, so we don't
  // re-trigger ourselves.
  (function () {
    var wrappedFetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch(input, init);
      if (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1) {
        result.then(function () { setTimeout(sync, 400); });
      }
      return result;
    };
    window.fetch = wrappedFetch;
  })();

  // ---- Discount code field (cart drawer, optional) ------------------
  // Only matters if you're using Discount Codes rather than Automatic
  // Discounts. Applying a code to the cart session isn't exposed through
  // the Ajax Cart API — the reliable mechanism is Shopify's own
  // /discount/{code}?redirect={path} route, which attaches the code and
  // redirects back. That's a full page load, so we remember what the
  // customer typed (sessionStorage) purely to show a friendly
  // "applied" / "not valid" message once the page comes back.
  function applyDiscountCode(rawCode) {
    var code = (rawCode || '').trim();
    if (!code) return;
    try { sessionStorage.setItem(LAST_ATTEMPT_KEY, code); } catch (e) {}
    var redirect = window.location.pathname + window.location.search;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(redirect);
  }

  function handleDiscountSubmit(trigger) {
    var wrapper = trigger.closest('[data-discount-code-wrapper]');
    var input = wrapper && wrapper.querySelector('[data-discount-code-input]');
    if (!input) return;
    trigger.setAttribute('disabled', 'disabled');
    applyDiscountCode(input.value);
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-discount-code-submit]');
    if (!trigger) return;
    e.preventDefault();
    handleDiscountSubmit(trigger);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest && e.target.closest('[data-discount-code-input]');
    if (!input) return;
    e.preventDefault();
    var wrapper = input.closest('[data-discount-code-wrapper]');
    var submit = wrapper && wrapper.querySelector('[data-discount-code-submit]');
    if (submit) {
      handleDiscountSubmit(submit);
    } else {
      applyDiscountCode(input.value);
    }
  });

  function showDiscountFeedback(cart) {
    var attempted = null;
    try { attempted = sessionStorage.getItem(LAST_ATTEMPT_KEY); } catch (e) { return; }
    if (!attempted) return;

    var wrapper = document.querySelector('[data-discount-code-wrapper]');
    var msgEl = wrapper && wrapper.querySelector('[data-discount-code-message]');

    var match = (cart.discount_codes || []).filter(function (d) {
      return d && d.code && d.code.toUpperCase() === attempted.toUpperCase();
    })[0];

    if (msgEl) {
      if (match && match.applicable) {
        msgEl.hidden = false;
        msgEl.textContent = 'Code applied — your free gift card has been added!';
        msgEl.classList.remove('cart-drawer__discount-code-message--error');
        msgEl.classList.add('cart-drawer__discount-code-message--success');
      } else if (match && !match.applicable) {
        msgEl.hidden = false;
        msgEl.textContent = 'That code isn’t valid for this order (check the minimum spend and your region).';
        msgEl.classList.remove('cart-drawer__discount-code-message--success');
        msgEl.classList.add('cart-drawer__discount-code-message--error');
      } else {
        msgEl.hidden = true;
      }
    }

    try { sessionStorage.removeItem(LAST_ATTEMPT_KEY); } catch (e) {}
  }
})();
